import { useState, useEffect } from 'react';
import {
  Card, Avatar, Typography, Form, Input, Button, Divider,
  Tag, Row, Col, App, Space, Skeleton, Empty, Tabs,
} from 'antd';
import {
  UserOutlined, PhoneOutlined, LockOutlined,
  EditOutlined, SaveOutlined, CloseOutlined,
  CalendarOutlined, DollarOutlined, ShopOutlined,
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  EnvironmentOutlined, TeamOutlined, RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useAuthStore from '../../store/authStore';
import api from '../../api/axios';
import InvitationBuilder from '../../components/InvitationTemplates/InvitationBuilder';

const { Title, Text, Paragraph } = Typography;

const P = '#5F33E1';

const ROLE_META = {
  restoran:   { label: 'Restoran', color: 'orange'  },
  honanda:    { label: 'Honanda',  color: 'purple'  },
  tasvirchi:  { label: 'Tasvirchi', color: 'blue'   },
  customer:   { label: 'Mijoz',    color: 'green'   },
};

const ORDER_STATUS = {
  pending:    { label: 'Kutilmoqda',   color: '#faad14', icon: <ClockCircleOutlined /> },
  confirmed:  { label: 'Tasdiqlangan', color: '#52c41a', icon: <CheckCircleOutlined /> },
  approved:   { label: 'Tasdiqlangan', color: '#52c41a', icon: <CheckCircleOutlined /> },
  completed:  { label: 'Bajarilgan',   color: '#1890ff', icon: <CheckCircleOutlined /> },
  cancelled:  { label: 'Bekor qilingan', color: '#ff4d4f', icon: <CloseCircleOutlined /> },
  rejected:   { label: 'Rad etilgan',  color: '#ff4d4f', icon: <CloseCircleOutlined /> },
};

const formatPrice = (price) => {
  if (!price && price !== 0) return '0';
  return Number(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + " so'm";
};

const IMG_BASE = process.env.REACT_APP_URL || '';

const ProfilePage = () => {
  const { user, setUser } = useAuthStore();
  const { message } = App.useApp();

  const [editMode, setEditMode]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [pwdVisible, setPwdVisible] = useState(false);
  const [pwdSaving, setPwdSaving]   = useState(false);
  const [orders, setOrders]         = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const [profileForm] = Form.useForm();
  const [pwdForm]     = Form.useForm();

  const roleMeta = ROLE_META[user?.role] ?? { label: user?.role ?? '—', color: 'default' };

  // Fetch orders
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get('/booking/my');
      const data = res.data?.data || res.data || [];
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleEditStart = () => {
    profileForm.setFieldsValue({ fullname: user?.fullname });
    setEditMode(true);
  };

  const handleEditCancel = () => {
    setEditMode(false);
    profileForm.resetFields();
  };

  const handleProfileSave = async (values) => {
    setSaving(true);
    try {
      const res = await api.put('/user/profile', values);
      const updated = res.data.data ?? { ...user, ...values };
      setUser(updated);
      localStorage.setItem('user', JSON.stringify(updated));
      message.success("Profil muvaffaqiyatli yangilandi");
      setEditMode(false);
    } catch (err) {
      message.error(err.response?.data?.message ?? 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (values) => {
    setPwdSaving(true);
    try {
      await api.put('/user/change-password', {
        current_password: values.current_password,
        password:         values.password,
        password_confirmation: values.password_confirmation,
      });
      message.success("Parol muvaffaqiyatli o'zgartirildi");
      pwdForm.resetFields();
      setPwdVisible(false);
    } catch (err) {
      message.error(err.response?.data?.message ?? 'Xatolik yuz berdi');
    } finally {
      setPwdSaving(false);
    }
  };

  const initials = user?.fullname
    ? user.fullname.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : (user?.phone?.slice(-2) ?? 'U');

  // Order card renderer
  const renderOrderCard = (order) => {
    const st = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
    const serviceName = order.service?.name || order.tariff_name || 'Xizmat';
    const providerName = order.service?.business?.name || order.provider_name || '';
    const orderDate = order.date || order.created_at;
    const serviceImg = order.service?.images?.[0]?.image || order.service?.image;

    return (
      <div key={order.id} style={{
        background: 'var(--qk-card-bg)', borderRadius: 14, border: '1px solid var(--qk-border)',
        padding: 16, marginBottom: 10,
        transition: 'box-shadow 0.2s',
        cursor: 'default',
      }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(95,51,225,0.08)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {/* Service image */}
          <div style={{
            width: 64, height: 64, borderRadius: 12, overflow: 'hidden',
            background: '#f5f0ff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {serviceImg ? (
              <img src={IMG_BASE + serviceImg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ShopOutlined style={{ fontSize: 24, color: P }} />
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <Text strong style={{ fontSize: 15, lineHeight: 1.3 }}>{serviceName}</Text>
              <Tag
                icon={st.icon}
                color={st.color}
                style={{ borderRadius: 8, fontSize: 11, margin: 0, flexShrink: 0 }}
              >
                {st.label}
              </Tag>
            </div>

            {providerName && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                <ShopOutlined style={{ marginRight: 4 }} />{providerName}
              </Text>
            )}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              {orderDate && (
                <Text style={{ fontSize: 12, color: '#666' }}>
                  <CalendarOutlined style={{ marginRight: 4, color: P }} />
                  {dayjs(orderDate).format('DD.MM.YYYY')}
                </Text>
              )}
              {(order.start_datetime !== undefined && order.end_datetime !== undefined) && (
                <Text style={{ fontSize: 12, color: '#666' }}>
                  <ClockCircleOutlined style={{ marginRight: 4, color: P }} />
                  {order.start_datetime}:00 — {order.end_datetime}:00
                </Text>
              )}
              {order.quantity > 1 && (
                <Text style={{ fontSize: 12, color: '#666' }}>
                  <TeamOutlined style={{ marginRight: 4, color: P }} />
                  {order.quantity} kishi
                </Text>
              )}
            </div>

            {/* Price */}
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 15, color: P }}>
                <DollarOutlined style={{ marginRight: 4 }} />
                {formatPrice(order.total_price || order.price || 0)}
              </Text>
              {order.created_at && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(order.created_at).format('DD.MM.YYYY HH:mm')}
                </Text>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* ── Header card ── */}
      <Card
        style={{
          borderRadius: 16, marginBottom: 20, overflow: 'hidden',
          background: `linear-gradient(135deg, ${P}08, #8B5CF608)`,
          border: '1px solid var(--qk-border)',
        }}
        styles={{ body: { padding: '28px 32px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <Avatar
            size={80}
            src={user?.avatar}
            style={{
              background: `linear-gradient(135deg, ${P}, #8B5CF6)`,
              fontSize: 30, fontWeight: 700, flexShrink: 0,
              boxShadow: '0 4px 16px rgba(95,51,225,0.25)',
            }}
          >
            {!user?.avatar && initials}
          </Avatar>

          <div style={{ flex: 1 }}>
            <Title level={3} style={{ margin: 0, color: 'var(--qk-text)' }}>
              {user?.fullname || '—'}
            </Title>
            <Space size={8} style={{ marginTop: 6 }} wrap>
              <Text type="secondary" style={{ fontSize: 14 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {user?.phone}
              </Text>
              <Tag color={roleMeta.color} style={{ borderRadius: 8, fontWeight: 600 }}>
                {roleMeta.label}
              </Tag>
              {user?.is_employee && (
                <Tag color="geekblue" style={{ borderRadius: 8 }}>Xodim</Tag>
              )}
            </Space>
          </div>

          {!editMode && (
            <Button
              icon={<EditOutlined />}
              onClick={handleEditStart}
              style={{
                borderRadius: 10, fontWeight: 600,
                borderColor: P, color: P,
              }}
            >
              Tahrirlash
            </Button>
          )}
        </div>
      </Card>

      <Tabs
        defaultActiveKey="info"
        type="card"
        style={{ marginBottom: 20 }}
        items={[
          {
            key: 'info',
            label: <span><UserOutlined style={{ marginRight: 6 }} />Shaxsiy ma'lumotlar</span>,
            children: (
              <Row gutter={[16, 16]}>
                {/* ── Profile form ── */}
                <Col xs={24} md={12}>
                  <Card
                    style={{ borderRadius: 14, height: '100%' }}
                    styles={{ body: { padding: '20px 24px' } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <UserOutlined style={{ color: P, fontSize: 16 }} />
                      <Text strong style={{ fontSize: 15 }}>Profil ma'lumotlari</Text>
                    </div>
                    <Form
                      form={profileForm}
                      layout="vertical"
                      onFinish={handleProfileSave}
                      initialValues={{ fullname: user?.fullname }}
                    >
                      <Form.Item label="To'liq ism" name="fullname">
                        <Input
                          prefix={<UserOutlined style={{ color: '#bbb' }} />}
                          placeholder="Ism Familiya"
                          disabled={!editMode}
                          style={{ borderRadius: 10 }}
                        />
                      </Form.Item>

                      <Form.Item label="Telefon raqam">
                        <Input
                          prefix={<PhoneOutlined style={{ color: '#bbb' }} />}
                          value={user?.phone}
                          disabled
                          style={{ borderRadius: 10 }}
                        />
                      </Form.Item>

                      {editMode && (
                        <Form.Item style={{ marginBottom: 0 }}>
                          <Space>
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<SaveOutlined />}
                              loading={saving}
                              style={{
                                borderRadius: 10,
                                background: `linear-gradient(135deg, ${P}, #8B5CF6)`,
                                border: 'none',
                              }}
                            >
                              Saqlash
                            </Button>
                            <Button
                              icon={<CloseOutlined />}
                              onClick={handleEditCancel}
                              style={{ borderRadius: 10 }}
                            >
                              Bekor qilish
                            </Button>
                          </Space>
                        </Form.Item>
                      )}
                    </Form>
                  </Card>
                </Col>

                {/* ── Password change ── */}
                <Col xs={24} md={12}>
                  <Card
                    style={{ borderRadius: 14, height: '100%' }}
                    styles={{ body: { padding: '20px 24px' } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <LockOutlined style={{ color: P, fontSize: 16 }} />
                        <Text strong style={{ fontSize: 15 }}>Parolni o'zgartirish</Text>
                      </div>
                      {!pwdVisible && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setPwdVisible(true)}
                          style={{ color: P }}
                        >
                          O'zgartirish
                        </Button>
                      )}
                    </div>
                    {!pwdVisible ? (
                      <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        padding: '32px 0', color: '#bbb',
                      }}>
                        <LockOutlined style={{ fontSize: 36, marginBottom: 10, color: '#ddd' }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Parolni o'zgartirish uchun tugmani bosing
                        </Text>
                      </div>
                    ) : (
                      <Form form={pwdForm} layout="vertical" onFinish={handlePasswordChange}>
                        <Form.Item
                          label="Joriy parol"
                          name="current_password"
                          rules={[{ required: true, message: 'Joriy parolni kiriting' }]}
                        >
                          <Input.Password
                            prefix={<LockOutlined style={{ color: '#bbb' }} />}
                            placeholder="••••••••"
                            style={{ borderRadius: 10 }}
                          />
                        </Form.Item>

                        <Form.Item
                          label="Yangi parol"
                          name="password"
                          rules={[
                            { required: true, message: 'Yangi parolni kiriting' },
                            { min: 8, message: 'Kamida 8 ta belgi' },
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined style={{ color: '#bbb' }} />}
                            placeholder="••••••••"
                            style={{ borderRadius: 10 }}
                          />
                        </Form.Item>

                        <Form.Item
                          label="Yangi parolni tasdiqlang"
                          name="password_confirmation"
                          dependencies={['password']}
                          rules={[
                            { required: true, message: 'Parolni tasdiqlang' },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Parollar mos kelmaydi'));
                              },
                            }),
                          ]}
                        >
                          <Input.Password
                            prefix={<LockOutlined style={{ color: '#bbb' }} />}
                            placeholder="••••••••"
                            style={{ borderRadius: 10 }}
                          />
                        </Form.Item>

                        <Divider style={{ margin: '12px 0' }} />

                        <Space>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={pwdSaving}
                            style={{
                              borderRadius: 10,
                              background: `linear-gradient(135deg, ${P}, #8B5CF6)`,
                              border: 'none',
                            }}
                          >
                            Saqlash
                          </Button>
                          <Button
                            icon={<CloseOutlined />}
                            onClick={() => { pwdForm.resetFields(); setPwdVisible(false); }}
                            style={{ borderRadius: 10 }}
                          >
                            Bekor qilish
                          </Button>
                        </Space>
                      </Form>
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'orders',
            label: (
              <span>
                <CalendarOutlined style={{ marginRight: 6 }} />
                Buyurtmalarim
                {orders.length > 0 && (
                  <Tag
                    style={{
                      marginLeft: 8, borderRadius: 10, fontSize: 11,
                      background: P, color: '#fff', border: 'none',
                    }}
                  >
                    {orders.length}
                  </Tag>
                )}
              </span>
            ),
            children: (
              <div>
                {ordersLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[1, 2, 3].map(i => (
                      <Card key={i} style={{ borderRadius: 14 }}>
                        <Skeleton active avatar={{ shape: 'square', size: 64 }} paragraph={{ rows: 2 }} />
                      </Card>
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <Card style={{ borderRadius: 14, textAlign: 'center', padding: '40px 0' }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                            Hali buyurtmalar yo'q
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Xizmatlar sahifasidan birinchi buyurtmangizni bering
                          </Text>
                        </div>
                      }
                    />
                  </Card>
                ) : (
                  <div>
                    {/* Stats summary */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: 10, marginBottom: 16,
                    }}>
                      <div style={{
                        background: 'var(--qk-card-bg)', borderRadius: 12, padding: '14px 16px',
                        border: '1px solid var(--qk-border)',
                      }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Jami buyurtmalar</Text>
                        <Text strong style={{ fontSize: 20, color: P }}>{orders.length}</Text>
                      </div>
                      <div style={{
                        background: 'var(--qk-card-bg)', borderRadius: 12, padding: '14px 16px',
                        border: '1px solid var(--qk-border)',
                      }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Tasdiqlangan</Text>
                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                          {orders.filter(o => ['confirmed', 'approved', 'completed'].includes(o.status)).length}
                        </Text>
                      </div>
                      <div style={{
                        background: 'var(--qk-card-bg)', borderRadius: 12, padding: '14px 16px',
                        border: '1px solid var(--qk-border)',
                      }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Kutilmoqda</Text>
                        <Text strong style={{ fontSize: 20, color: '#faad14' }}>
                          {orders.filter(o => o.status === 'pending').length}
                        </Text>
                      </div>
                      <div style={{
                        background: 'var(--qk-card-bg)', borderRadius: 12, padding: '14px 16px',
                        border: '1px solid var(--qk-border)',
                      }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Umumiy summa</Text>
                        <Text strong style={{ fontSize: 14, color: P }}>
                          {formatPrice(orders.reduce((s, o) => s + (o.total_price || o.price || 0), 0))}
                        </Text>
                      </div>
                    </div>

                    {/* Order cards */}
                    {orders.map(renderOrderCard)}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'invitation',
            label: (
              <span>
                <EditOutlined style={{ marginRight: 6 }} />
                Taklifnoma
              </span>
            ),
            children: <InvitationBuilder />,
          },
        ]}
      />
    </div>
  );
};

export default ProfilePage;
